import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import Icon from "../../components/Icon";
import { Bluelagoon } from "@bluelagoon";

export default function IntegrationBuilderSummary({
    apiType, endpoints, approvedIds, errorMap, visibility, onPublish,
    integration, auth, credentials, isEditing, integrationId, draftId,
}) {
    const { t } = useTranslation();
    const [publishing, setPublishing] = useState(false);
    const [publishError, setPublishError] = useState(null);

    const isGql = apiType === "graphql";
    const readEndpoints = endpoints.filter((e) => isGql ? e.method === "QUERY" : e.method === "GET");
    const writeEndpoints = endpoints.filter((e) => isGql ? (e.method === "MUTATION" || e.method === "SUBSCRIPTION") : e.method !== "GET");

    const name = integration?.name || "API Integration";
    const baseUrl = integration?.baseUrl || "";
    const authType = auth?.type || integration?.auth?.type || "bearer";
    const authLabel = { bearer: "Bearer Token", basic: "Basic Auth", oauth2: "OAuth 2.0", api_key: "API Key", none: "None" }[authType] || authType;

    const isDraftEdit = isEditing && integration?.status === "draft";
    const effectiveVisibility = integration?.visibility || visibility || "public";

    async function runSave({ publish }) {
        setPublishing(true);
        setPublishError(null);
        try {
            const payload = {
                name, apiType, baseUrl,
                description: integration?.description || null,
                logoUrl: integration?.logoUrl || null,
                auth: auth || integration?.auth || { type: "none" },
                endpoints, errorMap, credentials,
                config: { classification: integration?.classification, scrapedUrls: integration?.scrapedUrls },
            };
            let res;
            if (publish) {
                // Publish path — flips draft → published, deducts credits if private
                res = await Bluelagoon.publishCommunityIntegration({ ...payload, visibility: effectiveVisibility, draftId: draftId || integrationId });
            } else if (isEditing && integrationId) {
                res = await Bluelagoon.updateCommunityIntegration({ ...payload, integrationId });
            } else {
                res = await Bluelagoon.publishCommunityIntegration({ ...payload, visibility: effectiveVisibility, draftId });
            }
            const data = res?.data?.data || res?.data;
            if (res?.status === 200 || data?.integration) {
                onPublish();
            } else {
                setPublishError(data?.message || "Failed to save");
            }
        } catch (err) {
            setPublishError(err.message);
        } finally {
            setPublishing(false);
        }
    }

    const handlePublish = () => runSave({ publish: !isEditing || isDraftEdit });
    const handleSaveDraft = () => runSave({ publish: false });

    return (
        <div className="max-w-2xl mx-auto space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-700">
            <div className="bg-white border rounded-xl p-6 space-y-5">
                <div className="flex items-center gap-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Icon name="CheckCircle" className="w-7 h-7 text-green-600" />
                    </div>
                    <div>
                        <h2 className="text-lg font-bold text-gray-900">{isEditing ? t("builder.summary.updateTitle") : t("builder.summary.readyTitle")}</h2>
                        <p className="text-sm text-gray-500">{isEditing ? t("builder.summary.updateSubtitle") : t("builder.summary.readySubtitle")}</p>
                    </div>
                </div>
                <div className="grid grid-cols-4 gap-3">
                    <Stat label={t("builder.summary.endpoints")} value={endpoints.length} icon="Layers" color="blue" />
                    <Stat label={t("builder.summary.approved")} value={approvedIds.length} icon="CheckCircle" color="green" />
                    <Stat label={t("builder.summary.skipped")} value={readEndpoints.length - approvedIds.length} icon="SkipForward" color="gray" />
                    <Stat label={t("builder.summary.errorMaps")} value={errorMap.length} icon="Shield" color="purple" />
                </div>
                <div className="bg-gray-50 rounded-lg p-4 space-y-2 text-sm">
                    <Row label={t("builder.summary.integrationName")} value={name} />
                    <Row label={t("builder.summary.apiType")} value={isGql ? "GraphQL" : "REST"} />
                    <Row label={t("builder.summary.baseUrl")} value={baseUrl} mono />
                    <Row label={t("builder.summary.authType")} value={authLabel} />
                    <Row label={isGql ? t("builder.summary.queries") : t("builder.summary.readApis")} value={`${readEndpoints.length} ${isGql ? t("builder.summary.queriesCount") : t("communityIntegrations.card.endpoints")}`} />
                    <Row label={isGql ? t("builder.summary.mutations") : t("builder.summary.writeApis")} value={`${writeEndpoints.length} ${isGql ? t("builder.summary.operationsCount") : t("communityIntegrations.card.endpoints")} (${t("builder.summary.untested")})`} />
                    {(!isEditing || isDraftEdit) && <Row label={t("builder.summary.visibility")} value={effectiveVisibility === "public" ? t("builder.summary.publicFree") : t("builder.summary.privateCost", { cost: 300 })} highlight={effectiveVisibility} />}
                </div>
                {publishError && <p className="text-sm text-red-600 text-center">{publishError}</p>}
                <button onClick={handlePublish} disabled={publishing} className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
                    <Icon name={isEditing && !isDraftEdit ? "Save" : (effectiveVisibility === "public" ? "Globe" : "Lock")} className="w-4 h-4" />
                    {publishing ? t("common.saving") : isEditing && !isDraftEdit ? t("common.saveChanges") : effectiveVisibility === "public" ? t("builder.summary.publishPublic") : t("builder.summary.publishPrivate", { cost: 300 })}
                </button>
                {isDraftEdit && (
                    <button onClick={handleSaveDraft} disabled={publishing} className="w-full py-2 text-sm text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors flex items-center justify-center gap-1.5 disabled:opacity-50">
                        <Icon name="Save" className="w-3.5 h-3.5" />
                        {t("builder.summary.saveDraft", { defaultValue: "Save as draft" })}
                    </button>
                )}
            </div>
        </div>
    );
}

const STAT_COLORS = { blue: "bg-blue-50 text-blue-600", green: "bg-green-50 text-green-600", gray: "bg-gray-100 text-gray-400", purple: "bg-purple-50 text-purple-600" };

function Stat({ label, value, icon, color }) {
    return (
        <div className={`rounded-lg p-3 text-center ${STAT_COLORS[color]}`}>
            <Icon name={icon} className="w-5 h-5 mx-auto mb-1" />
            <p className="text-lg font-bold">{value}</p>
            <p className="text-[10px]">{label}</p>
        </div>
    );
}

function Row({ label, value, mono, highlight }) {
    const hl = highlight === "public" ? "text-green-600" : highlight === "private" ? "text-amber-600" : "text-gray-900";
    return (
        <div className="flex justify-between">
            <span className="text-gray-500">{label}</span>
            <span className={`font-medium ${mono ? "font-mono text-xs" : ""} ${hl}`}>{value}</span>
        </div>
    );
}
